/**
 * Content validator for ContentUnit integrity checking
 * @module content/validator/ContentValidator
 */

import type {
  IValidator,
  ValidationResult,
  ValidationError,
  ValidationWarning,
  ValidationErrorCode,
  ValidationWarningCode,
} from './IValidator.js';
import type { ContentUnit } from '../types.js';

/**
 * Validates ContentUnit for structural integrity
 *
 * Checks for:
 * - Balanced indexed tags (`<1>...</1>`)
 * - Valid tag indices (no invalid/malformed tags)
 * - Style references (styles referenced by tags exist)
 * - Orphan styles (styles defined but not used)
 * - Basic ICU brace balance validation
 *
 * @example
 * ```typescript
 * const validator = new ContentValidator();
 *
 * // Valid unit
 * const valid = validator.validate({
 *   text: 'Hello <1>world</1>',
 *   styles: { '1': ['raw:<b>'] }
 * });
 * // → { valid: true, errors: [], warnings: [] }
 *
 * // Unbalanced tags
 * const invalid = validator.validate({
 *   text: 'Hello <1>world',
 *   styles: { '1': ['raw:<b>'] }
 * });
 * // → { valid: false, errors: [{ code: 'UNBALANCED_TAGS', ... }], ... }
 * ```
 */
export class ContentValidator implements IValidator {
  /**
   * Validate a ContentUnit for structural integrity
   *
   * @param unit - ContentUnit to validate
   * @returns Validation result with errors and warnings
   */
  validate(unit: ContentUnit): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Check tag balance
    this.validateTagBalance(unit.text, errors);

    // Check style references
    this.validateStyleReferences(unit.text, unit.styles, errors, warnings);

    // Check ICU brace balance (always, since text may contain ICU patterns)
    this.validateICUBraces(unit.text, errors);

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate that all indexed tags are properly balanced and correctly nested
   */
  private validateTagBalance(text: string, errors: ValidationError[]): void {
    // Pattern to match indexed tags: <1>, </1>, <1/>
    const tagPattern = /<(\/?)?(\d+)(\/)?>/g;
    // Stack-based approach to verify proper nesting order
    const tagStack: Array<{ index: string; position: number }> = [];
    let match;

    while ((match = tagPattern.exec(text)) !== null) {
      const [, closingSlash, indexStr, selfClosingSlash] = match;

      if (selfClosingSlash) {
        // Self-closing tag: <1/> - always valid
        continue;
      }

      if (closingSlash) {
        // Closing tag: </1>
        // Must match the most recently opened tag (proper nesting)
        if (tagStack.length === 0) {
          errors.push({
            code: 'UNBALANCED_TAGS' as ValidationErrorCode,
            message: `Closing tag </${indexStr}> has no matching opening tag`,
            path: `text[${match.index}]`,
          });
        } else {
          const lastOpen = tagStack[tagStack.length - 1];
          if (lastOpen.index === indexStr) {
            // Proper closing - pop the stack
            tagStack.pop();
          } else {
            // Check if this tag was opened earlier (improper nesting)
            const foundIndex = tagStack.findIndex((t) => t.index === indexStr);
            if (foundIndex >= 0) {
              // Tag was opened but not in the right order
              errors.push({
                code: 'UNBALANCED_TAGS' as ValidationErrorCode,
                message: `Improperly nested: </${indexStr}> should close before </${lastOpen.index}>`,
                path: `text[${match.index}]`,
              });
              // Remove the out-of-order tag from stack
              tagStack.splice(foundIndex, 1);
            } else {
              // Tag was never opened
              errors.push({
                code: 'UNBALANCED_TAGS' as ValidationErrorCode,
                message: `Closing tag </${indexStr}> has no matching opening tag`,
                path: `text[${match.index}]`,
              });
            }
          }
        }
      } else {
        // Opening tag: <1>
        tagStack.push({ index: indexStr, position: match.index });
      }
    }

    // Check for unclosed tags
    for (const { index } of tagStack) {
      errors.push({
        code: 'UNBALANCED_TAGS' as ValidationErrorCode,
        message: `Opening tag <${index}> has no matching closing tag`,
        path: 'text',
      });
    }
  }

  /**
   * Validate style references - check that used tags have styles and vice versa
   */
  private validateStyleReferences(
    text: string,
    styles: Record<string, string[]>,
    errors: ValidationError[],
    warnings: ValidationWarning[]
  ): void {
    // Find all tag indices used in text
    const usedIndices = new Set<string>();
    const tagPattern = /<\/?(\d+)(?:\/)?>/g;
    let match;

    while ((match = tagPattern.exec(text)) !== null) {
      usedIndices.add(match[1]);
    }

    // Check for used indices without style definitions
    for (const index of usedIndices) {
      if (!styles[index] || styles[index].length === 0) {
        // This is a warning, not an error - tag will render as <span>
        warnings.push({
          code: 'EMPTY_STYLE' as ValidationWarningCode,
          message: `Tag <${index}> has no style definition, will render as <span>`,
        });
      }
    }

    // Check for defined styles not used in text (orphan styles)
    for (const index of Object.keys(styles)) {
      if (!usedIndices.has(index)) {
        warnings.push({
          code: 'UNUSED_TAG' as ValidationWarningCode,
          message: `Style for tag <${index}> is defined but tag is not used in text`,
        });
      }
    }
  }

  /**
   * Validate ICU MessageFormat brace balance
   */
  private validateICUBraces(
    text: string,
    errors: ValidationError[]
  ): void {
    // Check for balanced braces
    let depth = 0;
    let inString = false;
    let stringChar = '';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const prevChar = i > 0 ? text[i - 1] : '';

      // Handle string escaping
      if (
        (char === '"' || char === "'") &&
        prevChar !== '\\'
      ) {
        if (!inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar) {
          inString = false;
          stringChar = '';
        }
        continue;
      }

      if (inString) continue;

      if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
        if (depth < 0) {
          errors.push({
            code: 'INVALID_ICU' as ValidationErrorCode,
            message: `Unbalanced ICU braces: unexpected '}' at position ${i}`,
            path: `text[${i}]`,
          });
          depth = 0; // Reset to continue checking
        }
      }
    }

    if (depth > 0) {
      errors.push({
        code: 'INVALID_ICU' as ValidationErrorCode,
        message: `Unbalanced ICU braces: ${depth} unclosed '{'`,
        path: 'text',
      });
    }

    // Check for empty variable names
    const emptyVarPattern = /\{\s*[,}]/;
    if (emptyVarPattern.test(text)) {
      errors.push({
        code: 'INVALID_ICU' as ValidationErrorCode,
        message: 'ICU variable name is empty or missing',
        path: 'text',
      });
    }
  }
}
